import { T, RADIUS } from '@/lib/tokens';
import { usePhotoLibrary } from '@/hooks/usePhotoLibrary';

interface PhotoLibraryPickerProps {
  category: string;
  onSelect: (url: string) => void;
  selectedUrl?: string;
}

function CheckmarkOverlay() {
  return (
    <div
      style={{
        position: 'absolute',
        top: 4,
        right: 4,
        width: 20,
        height: 20,
        borderRadius: '50%',
        background: T.accent,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="animate-pulse"
          style={{
            width: '100%',
            aspectRatio: '1',
            borderRadius: RADIUS.sm,
            background: T.surfaceSubtle,
          }}
        />
      ))}
    </div>
  );
}

export function PhotoLibraryPicker({ category, onSelect, selectedUrl }: PhotoLibraryPickerProps) {
  const { groups, isLoading, error } = usePhotoLibrary(category);

  if (isLoading) return <SkeletonGrid />;
  if (error) return (
    <p style={{ fontSize: 13, color: T.textMuted, textAlign: 'center', padding: '16px 0' }}>
      Could not load photo library. Try refreshing.
    </p>
  );
  if (groups.length === 0) return null;

  return (
    <div>
      {groups.map((group) => (
        <div key={group.label}>
          <p
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: T.textSec,
              marginBottom: 4,
              marginTop: 12,
            }}
          >
            {group.label}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {group.items.map((item) => {
              const isSelected = item.url === selectedUrl;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => item.url && onSelect(item.url)}
                  aria-label={item.name}
                  aria-pressed={isSelected}
                  style={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '1',
                    padding: 0,
                    borderRadius: RADIUS.sm,
                    border: `2px solid ${isSelected ? T.accentBorder : 'transparent'}`,
                    background: T.surfaceSubtle,
                    cursor: 'pointer',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={item.url}
                    alt={item.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: RADIUS.sm - 2,
                    }}
                  />
                  {isSelected && <CheckmarkOverlay />}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
